import { Intent } from "@blueprintjs/core";
import axios from "axios";
import { Filter, FilterButton } from "components/filtering";
import { FormLocationSelector, FormTextInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { NotificationToaster } from "components/notifications";
import { EmployeeStatus } from "components/status/EmployeeStatus";
import { UserHasAccess } from "components/user-access/UserAccess";
import { useDebounce } from "hooks/useDebounce/useDebounce";
import { useQuery } from "hooks/useQuery/useQuery";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EmployeeAssessmentsService, EmployeeService } from "services";
import { SetFiltering } from "state/actions/Actions";
import { riskTextColours } from "../riskColours";

const dimensionFilterKeys = {
    overall: "EMPLOYEEBANDING",
    hazardPerception: "HAZARDPERCEPTIONRISK",
    attitude: "ATTITUDERISK",
    knowledge: "KNOWLEDGERISK",
    observation: "OBSERVATIONRISK"
};

function renderRiskScore(risk) {
    if (!risk) {
        return <span>-</span>;
    }
    return <span style={{ color: riskTextColours[risk] }}>{risk}</span>;
}

export function EmployeeAssessmentsRisk() {
    const requiredAction = "EmployeeAssessments:View";
    const canAccessPage = UserHasAccess(requiredAction);

    const [isLoading, setIsLoading] = useState(true);
    const [initalSetupComplete, setInitalSetupComplete] = useState(false);

    const query = useQuery();
    const queryDimension = query.get("dimension");
    const queryBand = query.get("band");
    const queryCategory = query.get("category");
    const debounceTimeout = 750;
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const reduxLocations = useSelector(
        (state) => state.employeeAssessments.locations
    );
    const dispatch = useDispatch();
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);

    const tableHeaders = [
        "Employee Name",
        "Location",
        "Category",
        "Overall Risk",
        "Hazard Perception Risk",
        "Attitude Risk",
        "Knowledge Risk",
        "Observation Risk"
    ];

    const sortableHeaders = [
        { headerName: "Employee Name", sortName: "employeeFullName" },
        { headerName: "Location", sortName: "LocationName" },
        { headerName: "Category", sortName: "employeeCategoryName" },
        { headerName: "Overall Risk", sortName: "risk" },
        { headerName: "Hazard Perception Risk", sortName: "hazardPerceptionRisk" },
        { headerName: "Attitude Risk", sortName: "attitudeRisk" },
        { headerName: "Knowledge Risk", sortName: "knowledgeRisk" },
        { headerName: "Observation Risk", sortName: "observationRisk" }
    ];

    const [totalRecords, setTotalRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");

    const currentPageNumber = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();
    const currentSearchTerm = useRef();
    const locationFilterOpen = useRef(false);
    const inputRef = useRef(null);

    const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);
    const [employeeStatusOptions, setEmployeeStatusOptions] = useState([]);

    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        });
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onSelectedLocationsChange(locations) {
        dispatch({
            type: "SET_EMPLOYEE_ASSESSMENTS_LOCATIONS",
            payload: locations,
        });
    }

    function onLocationFilterOpened() {
        locationFilterOpen.current = true;
    }

    function onLocationFilterClosed() {
        locationFilterOpen.current = false;
        fetchTableData();
    }

    useEffect(() => {
        if (reduxLocations && reduxLocations.length > 0) {
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }
    }, [reduxLocations]);

    function buildDrillThroughFilters(employeeCategories) {
        const drillThroughFilters = [];

        if (queryDimension && queryBand) {
            const filterKey = dimensionFilterKeys[queryDimension];

            if (filterKey) {
                // The Filter component renders the dropdown button text from displayName,
                // so it must be the selected value's label rather than the filter group name.
                drillThroughFilters.push({
                    name: filterKey,
                    key: filterKey,
                    value: queryBand,
                    displayName: queryBand
                });
            }
        }

        if (queryCategory) {
            const matchingCategory = employeeCategories.find(
                category => String(category.id) === String(queryCategory)
            );

            drillThroughFilters.push({
                name: "EMPLOYEECATEGORY",
                key: "EMPLOYEECATEGORY",
                value: queryCategory,
                displayName: matchingCategory?.name ?? queryCategory
            });
        }

        return drillThroughFilters;
    }

    function mapFilters() {
        let mappedFilters = (currentAppliedFilters.current ?? []).map(function (f) {
            return {
                key: f.name,
                ...f,
            };
        });

        if (reduxLocations != null) {
            const mappedLocations = reduxLocations.map(function (l) {
                return { key: "Location", value: l };
            });

            mappedFilters = [...mappedFilters, ...mappedLocations];
        }

        return mappedFilters;
    }

    function fetchTableData(forceRefresh) {
        if (!reduxLocations) {
            setTableRows([]);
            setTotalRecords(0);
            setIsLoading(false);
            return;
        }

        if (canAccessPage && initalSetupComplete) {
            if (
                currentPageNumber.current === pageNumber &&
                currentTableSortBy.current === sortedBy &&
                currentTableSortDir.current === sortedDir &&
                currentSearchTerm.current === debouncedSearchTerm &&
                currentAppliedFilters.current === appliedFilters &&
                currentLocations.current === reduxLocations.length &&
                !forceRefresh
            ) {
                return;
            }

            currentPageNumber.current = pageNumber;
            currentTableSortBy.current = sortedBy;
            currentTableSortDir.current = sortedDir;
            currentSearchTerm.current = debouncedSearchTerm;
            currentAppliedFilters.current = appliedFilters;
            currentLocations.current = reduxLocations.length;

            setIsLoading(true);

            const mappedFilters = mapFilters();

            const pageSize = 50;

            const searchRequest = {
                requestCount: pageSize,
                pageNumber: pageNumber,
                sortField: sortedBy.sortName,
                sortDirection: sortedDir,
                filters: mappedFilters,
                searchTerm: debouncedSearchTerm,
            };

            loadData(searchRequest).then(
                () => {
                    setIsLoading(false);

                    setTimeout(function () {
                        if (inputRef.current != null) {
                            inputRef.current.focus();
                        }
                    });
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not retrieve employee assessments, please try again"
                    );
                }
            );
        }
    }

    const loadData = (searchRequest) => {
        return EmployeeAssessmentsService.getEmployeeOverviews(searchRequest).then(
            (r) => {
                let data = [];

                if (r?.data != null) {
                    data = r.data.map((d) => {
                        return [
                            <div key={d.employeeId} className="inline-items">
                                <EmployeeStatus
                                    statusId={d.status.id}
                                    typeId={d.type.id}
                                    typeAttributeMappings={employeeTypeOptions}
                                    statusAttributeMappings={employeeStatusOptions}
                                />
                                <a href={`/employee/${d.employeeId}/assessments`}>
                                    {d.employeeFullName}
                                </a>
                            </div>,
                            d.locationName,
                            d.category.name,
                            renderRiskScore(d.risk),
                            renderRiskScore(d.hazardPerceptionRisk),
                            renderRiskScore(d.attitudeRisk),
                            renderRiskScore(d.knowledgeRisk),
                            renderRiskScore(d.observationRisk)
                        ];
                    });
                }

                setTableRows(data);
                setTotalRecords(r.totalCount);
            }
        );
    };

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        appliedFilters,
        initalSetupComplete,
    ]);

    useEffect(() => {
        if (locationFilterOpen.current) {
            return;
        }
        fetchTableData(true);
    }, [reduxLocations]);

    useEffect(() => {
        axios
            .all([
                EmployeeService.getEmployeeOptions(),
                EmployeeAssessmentsService.getOptions(),
            ])
            .then(function ([employeeOptions, assessmentOptions]) {
                setEmployeeStatusOptions(employeeOptions.employeeStatuses);
                setEmployeeTypeOptions(employeeOptions.employeeTypes);

                const riskItems = assessmentOptions.riskScores.map(r => ({
                    id: r.name,
                    name: r.name
                }));

                setPageFilters([
                    {
                        name: "EMPLOYEESTATUS",
                        displayName: "Status",
                        items: employeeOptions.employeeStatuses.filter((status) => {
                            return status.active;
                        }),
                    },
                    {
                        name: "EMPLOYEETYPE",
                        displayName: "Type",
                        items: employeeOptions.employeeTypes,
                    },
                    {
                        name: "EMPLOYEECATEGORY",
                        displayName: "Category",
                        items: employeeOptions.employeeCategories,
                    },
                    {
                        name: "EMPLOYEEBANDING",
                        displayName: "Overall Risk",
                        items: riskItems,
                    },
                    {
                        name: "HAZARDPERCEPTIONRISK",
                        displayName: "Hazard Perception Risk",
                        items: riskItems,
                    },
                    {
                        name: "ATTITUDERISK",
                        displayName: "Attitude Risk",
                        items: riskItems,
                    },
                    {
                        name: "KNOWLEDGERISK",
                        displayName: "Knowledge Risk",
                        items: riskItems,
                    },
                    {
                        name: "OBSERVATIONRISK",
                        displayName: "Observation Risk",
                        items: riskItems,
                    },
                ]);

                // Apply drill-through filters in the same render as initalSetupComplete
                // — otherwise the fetchTableData effect fires once with empty filters
                // before setAppliedFilters propagates, racing the filtered request.
                const drillThroughFilters = buildDrillThroughFilters(employeeOptions.employeeCategories);
                if (drillThroughFilters.length > 0) {
                    setAppliedFilters(drillThroughFilters);
                    dispatch(SetFiltering("employeeAssessmentsRisk", drillThroughFilters));
                }

                setInitalSetupComplete(true);
            });
    }, []);

    return (
        <>
            <div className="spacer-bottom-small">
                <div className="filter-controls">
                    <div className="inline-items">
                        <div className="inline-item fill">
                            <div className="form-field-full">
                                <FormTextInput
                                    inputRef={inputRef}
                                    placeholder="Search Employees"
                                    onChange={onSearchChange}
                                    value={searchTerm}
                                    large
                                    disabled={isLoading}
                                    icon="search"
                                    id="assessment-risk-search-field"
                                />
                            </div>
                        </div>
                        <FormLocationSelector
                            businessArea={"Tacho"}
                            loading={!initalSetupComplete}
                            selectedLocations={reduxLocations}
                            setSelectedLocations={onSelectedLocationsChange}
                            onLocationListOpened={onLocationFilterOpened}
                            onLocationListClose={onLocationFilterClosed}
                            useLocationDefaults={useLocationDefaults}
                            useHierarchy={useHierarchy}
                            setUseHierarchy={setUseHierarchy}
                            locationDefaultsToTrue={isLoading ? null : true}
                            disabled={isLoading}
                        />
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>
                    <Filter
                        filterName={"employeeAssessmentsRisk"}
                        visible={showFilters}
                        filters={pageFilters}
                        onUpdate={onFilterChange}
                        onClickOutside={onToggleFilter}
                    />
                </div>
            </div>
            <ListingTable
                showPerPage={false}
                pageSize={50}
                headers={tableHeaders}
                data={tableRows}
                totalRecordCount={totalRecords}
                loadingData={isLoading}
                onPagingChange={onPagingChange}
                sortable
                sortableHeaders={sortableHeaders}
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                onSort={onTableSort}
            />
        </>
    );
}
